import React, { useMemo } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Grid,
    Stack,
    Chip,
    Button,
    Typography,
    useTheme,
    alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AnswerTypeFrontend, ListItemFieldDefinitionFrontend } from '../../../api/sessionQuestionsApi';

interface ListObjectDisplayProps {
    label: string;
    /** The answer value — a JSON string (or array) of item objects. */
    value: string;
    schema?: ListItemFieldDefinitionFrontend[];
    onEdit: () => void;
    editDisabled?: boolean;
}

/**
 * Read-only, expandable view of a LIST_OBJECT answer: an accordion whose header
 * shows the question label + item count + Edit, and whose body renders each
 * item's fields as a horizontal key→value grid — so recruiters can SEE the
 * answer without opening the edit modal.
 */
const ListObjectDisplay: React.FC<ListObjectDisplayProps> = ({ label, value, schema, onEdit, editDisabled }) => {
    const theme = useTheme();

    const items: Record<string, unknown>[] = useMemo(() => {
        try {
            const parsed = typeof value === 'string' && value ? JSON.parse(value) : value;
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }, [value]);

    // Prefer the schema for field order + labels; fall back to the first item's keys.
    const fields: ListItemFieldDefinitionFrontend[] = useMemo(() => {
        if (schema && schema.length) return schema;
        const first = items[0];
        if (!first) return [];
        return Object.keys(first).map((k) => ({ fieldName: k, label: k, fieldType: AnswerTypeFrontend.TEXT }));
    }, [schema, items]);

    const formatValue = (raw: unknown, field: ListItemFieldDefinitionFrontend): React.ReactNode => {
        if (raw === null || raw === undefined || raw === '') return '—';
        const resolveOpt = (k: unknown) => field.options?.find((o) => o.key === k)?.label ?? String(k);
        if (field.fieldType === AnswerTypeFrontend.BOOLEAN) return raw === true || raw === 'true' ? 'Yes' : 'No';
        if (field.fieldType === AnswerTypeFrontend.MULTISELECT || Array.isArray(raw)) {
            const arr = Array.isArray(raw) ? raw : [raw];
            if (arr.length === 0) return '—';
            return (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                    {arr.map((v, i) => <Chip key={i} size="small" label={resolveOpt(v)} sx={{ height: 20 }} />)}
                </Stack>
            );
        }
        if (field.fieldType === AnswerTypeFrontend.RADIO && field.options) return resolveOpt(raw);
        return String(raw);
    };

    return (
        <Box sx={{ position: 'relative' }}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
                width: '100%',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pr: 16 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{label}</Typography>
                    <Chip size="small" label={`${items.length} item${items.length !== 1 ? 's' : ''}`} sx={{ height: 20 }} />
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                {items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No items.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {items.map((item, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Grid container spacing={1.5}>
                                    {fields.map((f) => (
                                        <Grid item xs={12} sm={6} md={4} key={f.fieldName}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {f.label}
                                            </Typography>
                                            <Typography variant="body2" component="div">
                                                {formatValue(item[f.fieldName], f)}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                    </Stack>
                )}
            </AccordionDetails>
          </Accordion>
          <Button
              size="small"
              variant="outlined"
              onClick={onEdit}
              disabled={editDisabled}
              sx={{ position: 'absolute', right: 48, top: 8, zIndex: 1 }}
          >
              Edit Items
          </Button>
        </Box>
    );
};

export default ListObjectDisplay;
